﻿import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Maximize2, Minimize2, Plus, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { useCompany } from "@/lib/CompanyProvider";
import { useCustomerData } from "@/hooks/customers/useCustomerData";
import type { Customer, Vehicle } from "@/hooks/customers/useCustomerData";
import { CustomerList } from "@/components/customers/CustomerList";
import { CustomerModal } from "@/components/customers/CustomerModal";
import { VehicleModal } from "@/components/customers/VehicleModal";

export const Route = createFileRoute("/app/customers")({
  component: CustomersModule,
});

function CustomersModule() {
  const { activeCompanyId } = useCompany();

  const [searchTerm, setSearchTerm] = useState("");
  const [isCompact, setIsCompact] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [addingVehicleTo, setAddingVehicleTo] = useState<string | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  const { customers, vehicles, orders, loading, saveCustomer, saveVehicle } =
    useCustomerData(activeCompanyId);

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return customers.filter((c) => {
      const matchName = c.name?.toLowerCase().includes(term);
      const matchPhone = c.phone?.toLowerCase().includes(term);
      const custVehicles = vehicles.filter((v) => v.customer_id === c.id);
      const matchPlate = custVehicles.some((v) =>
        v.plate?.toLowerCase().includes(term),
      );
      return matchName || matchPhone || matchPlate;
    });
  }, [customers, vehicles, searchTerm]);

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg gap-4">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex w-full md:w-auto gap-3">
            <Skeleton className="h-10 w-full md:w-72" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={`cust-skel-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <Skeleton className="w-12 h-12 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-8 w-32 rounded-full" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="text-blue-500" size={32} />
            Gestão de Clientes
          </h1>
          <p className="text-slate-400 mt-1">
            Conheça sua carteira, histórico de lavagens e classifique seus VIPs.
          </p>
        </div>
        <div className="flex w-full md:w-auto gap-3">
          <div className="relative flex-1 md:w-72">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              size={18}
            />
            <Input
              placeholder="Buscar nome, placa ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white w-full"
            />
          </div>
          <Button
            onClick={() => setIsCompact(!isCompact)}
            variant="outline"
            className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
            title="Alternar Modo Compacto"
          >
            {isCompact ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
          </Button>
          <Button
            onClick={() =>
              setEditingCustomer({
                id: "",
                name: "",
                phone: "",
                email: "",
                created_at: "",
              })
            }
            className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-900/20"
          >
            <Plus size={18} className="mr-2" /> Novo
          </Button>
        </div>
      </div>

      <CustomerList
        filteredCustomers={filteredCustomers}
        vehicles={vehicles}
        orders={orders}
        isCompact={isCompact}
        setEditingCustomer={setEditingCustomer}
        setAddingVehicleTo={setAddingVehicleTo}
        setEditingVehicle={setEditingVehicle}
      />

      <CustomerModal
        editingCustomer={editingCustomer}
        setEditingCustomer={setEditingCustomer}
        saveCustomer={saveCustomer}
      />

      <VehicleModal
        addingVehicleTo={addingVehicleTo}
        setAddingVehicleTo={setAddingVehicleTo}
        editingVehicle={editingVehicle}
        setEditingVehicle={setEditingVehicle}
        saveVehicle={saveVehicle}
      />
    </div>
  );
}
